import { useState, useCallback, useRef } from 'react';
import getCompanyIdForRequest from '@/lib/getCompanyIdForRequest';
import xMindcloudInstance from '@/schema/xMindcloudInstance';

const local = window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1');
const staging = window.location.hostname.includes('staging');
const BASE_API = local ? 'http://localhost:9800' : staging ? 'https://api-staging.mindcloud.co' : 'https://api.mindcloud.co';

const useFileUpload = ({ mode = 'internal', shareToken = null } = {}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const xhrRef = useRef(null);

  const upload = useCallback(
    async (files) => {
      if (!files || files.length === 0) {
        return { success: false, data: [] };
      }

      setIsUploading(true);
      setProgress(0);
      setError(null);

      const isShareMode = mode === 'public';
      if (isShareMode && !shareToken) {
        setIsUploading(false);
        setError('Missing share token');
        return { success: false, message: 'Missing share token' };
      }

      const bearerToken = isShareMode ? null : await window?.Clerk?.session?.getToken();
      const companyId = isShareMode ? null : getCompanyIdForRequest();

      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      if (isShareMode) {
        formData.append('token', shareToken);
      }

      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setProgress(pct);
          }
        });

        xhr.addEventListener('load', () => {
          setIsUploading(false);
          xhrRef.current = null;

          try {
            const response = JSON.parse(xhr.responseText);

            if (xhr.status >= 200 && xhr.status < 300 && response.success) {
              setProgress(100);
              resolve(response);
            } else {
              const msg = response.message || 'Upload failed';
              setError(msg);
              resolve({ success: false, message: msg });
            }
          } catch {
            setError('Failed to parse upload response');
            resolve({ success: false, message: 'Failed to parse upload response' });
          }
        });

        xhr.addEventListener('error', () => {
          setIsUploading(false);
          xhrRef.current = null;
          setError('Upload failed due to a network error');
          resolve({ success: false, message: 'Network error' });
        });

        xhr.addEventListener('abort', () => {
          setIsUploading(false);
          xhrRef.current = null;
          resolve({ success: false, message: 'Upload cancelled' });
        });

        xhr.open('POST', `${BASE_API}/v1/files`);
        xhr.setRequestHeader('x-mindcloud-instance', xMindcloudInstance);

        if (!isShareMode) {
          xhr.setRequestHeader('Authorization', `Bearer ${bearerToken}`);
          if (companyId) {
            xhr.setRequestHeader('X-Company-Id', companyId);
          }
        }

        xhr.send(formData);
      });
    },
    [mode, shareToken]
  );

  const cancel = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort();
    }
  }, []);

  return { upload, cancel, isUploading, progress, error };
};

export default useFileUpload;
